/** Shared questionnaire hierarchy. Browser-safe: no database or Notion imports. */
export interface QuestionnaireSection { id: number; title: string; position: number }
export interface StructureQuestion {
  id: number; text: string; position: number;
  sectionId: number | null; parentQuestionId: number | null;
}
export interface DisplayQuestion extends StructureQuestion { label: string }
export interface QuestionnaireStructure {
  groups: { section: QuestionnaireSection | null; questions: { question: DisplayQuestion; subQuestions: DisplayQuestion[] }[] }[];
  flatQuestions: DisplayQuestion[];
  sections: QuestionnaireSection[];
}
const ordered = <T extends {position:number;id:number}>(rows: readonly T[]): T[] =>
  [...rows].sort((a,b)=>a.position-b.position || a.id-b.id);

export function assertQuestionnaireStructure(sections: readonly QuestionnaireSection[], questions: readonly StructureQuestion[]): void {
  const sectionIds = new Set(sections.map(s=>s.id));
  const byId = new Map(questions.map(q=>[q.id,q]));
  if (sectionIds.size !== sections.length || byId.size !== questions.length) throw new Error("Duplicate questionnaire IDs");
  for (const row of [...sections,...questions]) {
    if (!Number.isSafeInteger(row.id) || row.id < 1 || !Number.isFinite(row.position)) throw new Error("Invalid questionnaire ID or position");
  }
  for (const q of questions) {
    if (q.sectionId !== null && !sectionIds.has(q.sectionId)) throw new Error("Section is not in this questionnaire");
    if (q.parentQuestionId === null) continue;
    const parent = byId.get(q.parentQuestionId);
    if (!parent || parent.id === q.id) throw new Error("Invalid parent question");
    if (parent.parentQuestionId !== null) throw new Error("Sub-questions cannot have sub-questions");
    if (parent.sectionId !== q.sectionId) throw new Error("Sub-question must belong to its parent's section");
  }
}
function suffix(index:number):string {
  let value = index+1, text="";
  while(value>0) { value--;text=String.fromCharCode(97+value%26)+text;value=Math.floor(value/26); }
  return text;
}
export function buildQuestionnaireStructure(sections: readonly QuestionnaireSection[], questions: readonly StructureQuestion[]): QuestionnaireStructure {
  assertQuestionnaireStructure(sections,questions);
  const groups:QuestionnaireStructure["groups"]=[], flatQuestions:DisplayQuestion[]=[];
  let number=0;
  for (const section of [null,...ordered(sections)]) {
    const roots=ordered(questions.filter(q=>q.parentQuestionId===null && q.sectionId===(section?.id??null)));
    if (!roots.length) continue;
    const group:QuestionnaireStructure["groups"][number] = {section,questions:[]};
    for (const root of roots) {
      number++;
      const question:DisplayQuestion={...root,position:flatQuestions.length+1,label:String(number)};
      flatQuestions.push(question);
      const subQuestions=ordered(questions.filter(q=>q.parentQuestionId===root.id)).map((child,index)=>{
        const display={...child,position:flatQuestions.length+1,label:`${number}${suffix(index)}`};
        flatQuestions.push(display);return display;
      });
      group.questions.push({question,subQuestions});
    }
    groups.push(group);
  }
  return {groups,flatQuestions,sections:groups.flatMap(g=>g.section?[g.section]:[])};
}
